import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';
import { CompanyProvider } from './contexts/CompanyContext';
import { SocketProvider } from './contexts/SocketContext';
import { PermissionsProvider } from './contexts/PermissionsContext';
import { ConfirmProvider } from './components/ConfirmDialog';
import UpdateAvailableBanner from './components/UpdateAvailableBanner';
import App from './App';
import './index.css';

// Nothing here is cached as "fresh". staleTime 0 + refetch-always means every
// mount, tab focus and reconnect re-reads from the API, so an edit made in one
// screen (or by someone else) shows up the moment you look at another. Cached
// data is still rendered instantly while the refetch runs — this removes the
// stale window, not the instant paint.
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 0,
      retry: 1,
      refetchOnMount: 'always',
      refetchOnWindowFocus: 'always',
      refetchOnReconnect: 'always',
    },
  },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <AuthProvider>
          <CompanyProvider>
            <SocketProvider>
              <PermissionsProvider>
                <ConfirmProvider>
                  <App />
                  <Toaster position="top-right" />
                  {/* Global: offers a refresh to any tab left open across a deploy */}
                  <UpdateAvailableBanner />
                </ConfirmProvider>
              </PermissionsProvider>
            </SocketProvider>
          </CompanyProvider>
        </AuthProvider>
      </BrowserRouter>
      </ThemeProvider>
    </QueryClientProvider>
  </React.StrictMode>
);
